import { DownloadOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Drawer, Flex, Form, Input, Modal, Popconfirm, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { api, unwrap, type Schemas, type Warehouse } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { usePoll } from "@/components/tabs/WorkTabs";

type Recorder = Schemas["Recorder"];

function online(r: Recorder) {
  return r.status === "active" && !!r.lastSeenAt && Date.parse(r.lastSeenAt) > Date.now() - 3 * 60_000;
}

/**
 * Perangkat perekam: packing laptops running the recorder agent. A laptop is
 * paired with a one-time code typed into the agent; it can be removed here.
 */
export function RecordersDrawer({ warehouse, onClose }: { warehouse: Warehouse | null; onClose: () => void }) {
  const { t, formatDateTime } = useI18n();
  const { me } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const tz = me?.workspace?.timezone;
  const [adding, setAdding] = useState(false);
  const [code, setCode] = useState<Schemas["RecorderPairing"] | null>(null);
  const key = ["recorders", warehouse?.id];
  const list = useQuery({
    queryKey: key,
    queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/recorders", { params: { path: { warehouseID: warehouse!.id } } })),
    enabled: warehouse !== null,
    refetchInterval: usePoll(15_000),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => unwrap(api.POST("/v1/recorders/{deviceID}/revoke", { params: { path: { deviceID: id } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
    onError: (err) => void message.error(errorText(err)),
  });
  const setHub = useMutation({
    mutationFn: ({ id, hub }: { id: string; hub: boolean }) =>
      unwrap(api.POST("/v1/recorders/{deviceID}/hub", { params: { path: { deviceID: id } }, body: { hub } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
    onError: (err) => void message.error(errorText(err)),
  });
  const gb = (n: number | null) => (n == null ? "—" : `${(n / 1e9).toFixed(1)} GB`);
  return (
    <Drawer
      open={warehouse !== null}
      onClose={onClose}
      size="large"
      title={warehouse ? t("rec.title", { name: warehouse.name }) : ""}
      destroyOnHidden
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>
          {t("rec.add")}
        </Button>
      }
    >
      <Typography.Paragraph type="secondary">{t("rec.help")}</Typography.Paragraph>
      <Typography.Paragraph type="secondary">{t("rec.hubHelp")}</Typography.Paragraph>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        title={t("rec.install")}
        description={t("rec.installSteps")}
        action={
          <Button icon={<DownloadOutlined />} href={list.data?.download ?? "/downloads/zerpid-agent.zip"}>
            {t("rec.download", { version: list.data?.latestVersion ?? "" })}
          </Button>
        }
      />
      {(list.data?.pairings ?? []).length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={t("rec.pending", { names: (list.data?.pairings ?? []).map((p) => p.name).join(", ") })}
        />
      )}
      <Table<Recorder>
        rowKey="id"
        size="small"
        loading={list.isPending}
        dataSource={list.data?.items ?? []}
        pagination={false}
        locale={{ emptyText: t("rec.empty") }}
        columns={[
          {
            title: t("rec.device"),
            render: (_, r) => (
              <Space orientation="vertical" size={0}>
                <Space size={6}>
                  <Typography.Text strong>{r.name}</Typography.Text>
                  {r.hub && r.status === "active" && <Tag color="blue">{t("rec.hub")}</Tag>}
                  {r.status === "revoked" ? <Tag>{t("rec.revoked")}</Tag> : online(r) ? <Tag color="success">{t("rec.online")}</Tag> : <Tag color="warning">{t("rec.offline")}</Tag>}
                </Space>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {[r.camera || t("rec.noCamera"), r.version && `v${r.version}`].filter(Boolean).join(" · ")}
                </Typography.Text>
                {r.hub && r.status === "active" && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {r.lanAddrs.length > 0 ? t("rec.hubAddrs", { addrs: r.lanAddrs.join(", ") }) : t("rec.hubWaiting")}
                  </Typography.Text>
                )}
              </Space>
            ),
          },
          { title: t("rec.lastSeen"), dataIndex: "lastSeenAt", width: 170, render: (v: string | null) => (v ? formatDateTime(v, tz) : "—") },
          {
            title: t("rec.clips"),
            width: 160,
            render: (_, r) => (
              <Space orientation="vertical" size={0}>
                <span className="z-num">{t("rec.clipsValue", { n: r.clips, free: gb(r.freeBytes) })}</span>
                {r.uploadsWaiting > 0 && <Typography.Text type="warning" style={{ fontSize: 12 }}>{t("rec.uploadsWaiting", { n: r.uploadsWaiting })}</Typography.Text>}
              </Space>
            ),
          },
          {
            width: 210,
            render: (_, r) =>
              r.status === "active" && (
                <Space>
                  <Popconfirm
                    title={t(r.hub ? "rec.unhubConfirm" : "rec.makeHubConfirm", { name: r.name })}
                    onConfirm={() => setHub.mutate({ id: r.id, hub: !r.hub })}
                  >
                    <Button size="small">{t(r.hub ? "rec.unhub" : "rec.makeHub")}</Button>
                  </Popconfirm>
                  <Popconfirm title={t("rec.revokeConfirm", { name: r.name })} onConfirm={() => revoke.mutate(r.id)}>
                    <Button size="small" danger>
                      {t("rec.revoke")}
                    </Button>
                  </Popconfirm>
                </Space>
              ),
          },
        ]}
      />
      <AddModal
        open={adding}
        warehouseId={warehouse?.id ?? ""}
        onClose={() => setAdding(false)}
        onCode={(c) => {
          setAdding(false);
          setCode(c);
          void queryClient.invalidateQueries({ queryKey: key });
        }}
      />
      <Modal open={code !== null} title={t("rec.codeTitle", { name: code?.name ?? "" })} footer={<Button onClick={() => setCode(null)}>{t("common.close")}</Button>} onCancel={() => setCode(null)}>
        <Flex vertical align="center" gap={8} style={{ margin: "16px 0" }}>
          <Typography.Text className="z-num" style={{ fontSize: 36, fontWeight: 700, letterSpacing: 4 }} copyable>
            {code?.code}
          </Typography.Text>
          <Typography.Text type="secondary">{code && t("rec.codeExpires", { at: formatDateTime(code.expiresAt, tz) })}</Typography.Text>
        </Flex>
        <Typography.Paragraph>{t("rec.codeSteps")}</Typography.Paragraph>
      </Modal>
    </Drawer>
  );
}

function AddModal({ open, warehouseId, onClose, onCode }: { open: boolean; warehouseId: string; onClose: () => void; onCode: (c: Schemas["RecorderPairing"]) => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<{ name: string }>();
  const make = useMutation({
    mutationFn: (v: { name: string }) =>
      unwrap(api.POST("/v1/warehouses/{warehouseID}/recorders/pairing", { params: { path: { warehouseID: warehouseId } }, body: { name: v.name.trim() } })),
    onSuccess: onCode,
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal open={open} title={t("rec.add")} okText={t("rec.makeCode")} cancelText={t("common.cancel")} confirmLoading={make.isPending} onOk={() => form.submit()} onCancel={onClose} destroyOnHidden>
      <Form form={form} layout="vertical" requiredMark={false} onFinish={(v) => make.mutate(v)}>
        <Form.Item name="name" label={t("rec.deviceName")} extra={t("rec.deviceNameHelp")} rules={[{ required: true, message: t("field.required") }]}>
          <Input maxLength={80} placeholder={t("rec.deviceNamePlaceholder")} autoFocus />
        </Form.Item>
      </Form>
    </Modal>
  );
}
